import { Component, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, ReactiveFormsModule } from '@angular/forms';

import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { first, map } from 'rxjs';
import { RegionalEcosystemStatusCodeEnum } from 'src/app/models/shared-models';
import { BaseWizardComponent } from '../base-wizard.component';
import { ICategoryPurposeRegionalEcosystemStatusModel, IRegionalEcosystemStatusModel, RegionalEcosystemStatusDataModel, SelectedStatusGroupModel } from '../models/veg-wizard-models';
import { RegionalEcosystemStatusService } from '../services/regional-ecosystem-status.service';
import { VegWizardDataService } from '../services/veg-wizard-data.service';

@UntilDestroy()
@Component({
  selector: 'app-select-regional-ecosystem-status',
  templateUrl: './select-regional-ecosystem-status.component.html',
  styleUrls: ['./select-regional-ecosystem-status.component.css'],
  imports: [
    ReactiveFormsModule
],
})
export class SelectRegionalEcosystemStatusComponent extends BaseWizardComponent implements OnInit {

  form: UntypedFormGroup;
  regionalEcosystemStatusData: IRegionalEcosystemStatusModel;

  constructor(private formBuilder: UntypedFormBuilder,
    router: Router,
    private vwds: VegWizardDataService,
    private regionalEcosystemStatusService: RegionalEcosystemStatusService) {
    super();
    this.regionalEcosystemStatusData = this.vwds.getRegionalEcosystemStatuses() ?? new RegionalEcosystemStatusDataModel();
    this.form = this.formBuilder.group({ regionalEcosystemStatusArray: this.formBuilder.array([]) });
  }

  ngOnInit(): void {
    this.setAvailableRegionalEcosystemStatusControls();
  }

  //For each category create a list of statuses with checkbox controls
  setAvailableRegionalEcosystemStatusControls() {
    this.regionalEcosystemStatusService.getAvailableRegionalEcosystemStatuses$().pipe(first(), untilDestroyed(this),
      map(result => {

        const distinctCategories = [...new Map(result.map(activity =>
          [activity['category'], activity.category])).values()];

        //Remove koala habitat option if the lot plan is not in a koala habitat area
        const lotOnPlan = this.vwds.getLotPlanModel();
        if (!lotOnPlan?.containsKoalaHabitatArea && !lotOnPlan?.containsLocallyRefinedKoalaHabitatArea) {
          if (result.some(x => x.code === RegionalEcosystemStatusCodeEnum.koalaHabitat)) {
            const resultWithoutKoalaHabitat = result.filter(x => x.code !== RegionalEcosystemStatusCodeEnum.koalaHabitat);
            result = resultWithoutKoalaHabitat;            
          }
        }

        distinctCategories.forEach(category => {
          const statusArray = this.formBuilder.array([]);
          result.filter(regionalEcosystemStatus => regionalEcosystemStatus.category == category).forEach(status => {

            const checkBoxValue = this.regionalEcosystemStatusData
              ? this.regionalEcosystemStatusData.regionalEcosystemStatusIds.some(statusId => statusId == status.regionalEcosystemStatusId)
              : false;
            const statusFormGroup = this.formBuilder.group({ status: status, checked: new UntypedFormControl(checkBoxValue) });

            statusArray.push(statusFormGroup);
            statusFormGroup.valueChanges.pipe(untilDestroyed(this))
              .subscribe(statusFormGroup => {
                this.setRegionalEcosystemStatusData(statusFormGroup.status, statusFormGroup.checked);
              });
          });
          this.getRegionalEcosystemStatusArray().push(this.formBuilder.group({
            category: category,
            statuses: statusArray,
          }));
        });
      })).subscribe();
  }

  //Add or remove the selected status to the data model
  setRegionalEcosystemStatusData(selectedStatus: ICategoryPurposeRegionalEcosystemStatusModel, isChecked: boolean) {
    isChecked
      ? this.regionalEcosystemStatusData.regionalEcosystemStatusIds.push(selectedStatus.regionalEcosystemStatusId)
      : this.regionalEcosystemStatusData.regionalEcosystemStatusIds.splice(this.regionalEcosystemStatusData.regionalEcosystemStatusIds.indexOf(selectedStatus.regionalEcosystemStatusId), 1);

    const currentStatusGroup = this.regionalEcosystemStatusData.selectedStatusGroups.filter(status => status.category == selectedStatus.category)[0];

    currentStatusGroup ?
      isChecked
        ? currentStatusGroup.statuses.push(selectedStatus)
        : currentStatusGroup.statuses?.splice(currentStatusGroup.statuses.indexOf(selectedStatus), 1)
      : this.regionalEcosystemStatusData.selectedStatusGroups.push({ category: selectedStatus.category, statuses: [selectedStatus] } as SelectedStatusGroupModel);

    this.setDistinctMessagesList();
  }

  //Replace the current list of messages with a distinct list of messages from the currently selected statuses
  setDistinctMessagesList() {
    const messages: string[] = [];
    this.regionalEcosystemStatusData.selectedStatusGroups.forEach(statusGroup => statusGroup.statuses.forEach(status => {

      if (status.message) {
        messages.push(status.message);
      }
    }));
    this.regionalEcosystemStatusData.messages = [...new Set(messages)];
  }

  atLeastOnestatusSelectedForEachCategoryValidator(): ValidatorFn {
    //Default to false if there are no regional ecosystem arrays as this means the user hasn't done anything yet
    let statusSelectedForEachCategory = this.getRegionalEcosystemStatusArray().controls.length != 0;
    this.getRegionalEcosystemStatusArray().controls.forEach(statusArrayControl => {
      if (!this.getStatusArray(statusArrayControl).controls.some(statusControl => {
        return statusControl.value.checked;
      })) {
        statusSelectedForEachCategory = false;
      }
    });
    return () => {
      return statusSelectedForEachCategory ? null : { statusRequiredValidationError: true };
    };
  }

  statusBlocksNotificationValidator(): ValidatorFn {
    const blockingStatusSelected = this.getRegionalEcosystemStatusArray().controls.some(statusArrayControl => {
      return this.getStatusArray(statusArrayControl).controls.some(statusControl => {
        return statusControl.value.checked && statusControl.value.status.blockNotificationIfSelected;
      });
    });
    return () => {
      return blockingStatusSelected ? { notificationBlockedValidationError: true } : null;
    };
  }

  getRegionalEcosystemStatusArray(): UntypedFormArray {
    return this.form.get('regionalEcosystemStatusArray') as UntypedFormArray;
  }

  getStatusArray(control: AbstractControl): UntypedFormArray {
    return control.get('statuses') as UntypedFormArray;
  }

  override next(): void {
    this.form.setValidators([this.atLeastOnestatusSelectedForEachCategoryValidator(), this.statusBlocksNotificationValidator()]);
    this.form.markAsDirty()
    this.form.updateValueAndValidity();

    if (!this.form.valid) return;

    this.vwds.setRegionalEcosystemStatuses$(this.regionalEcosystemStatusData)
      .pipe(
        first(),
        untilDestroyed(this)
      )
      .subscribe(() => {
        super.next();
      });
  }

  override cancel(): void {
    super.cancel();
    this.vwds.clear().pipe(first(), untilDestroyed(this)).subscribe();
  }
}
